import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Calculator, Crown, Search, Plus, Minus, Trash2, ShoppingCart, Boxes, Receipt, Wallet, ArrowLeft, RefreshCw, Printer } from "lucide-react";
import { api, apiErr, inr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useNavigate } from "react-router-dom";

export default function SellerPOS() {
  const nav = useNavigate();
  const [sub, setSub] = useState(null);
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [cart, setCart] = useState({});
  const [sales, setSales] = useState({ sales: [], today_total: 0, today_count: 0 });
  const [busy, setBusy] = useState(false);
  const [topupAmt, setTopupAmt] = useState(100);
  const [renew, setRenew] = useState(false);
  const [hist, setHist] = useState([]);
  const [bill, setBill] = useState(null);

  const loadSub = () => api.get("/seller-pos/subscription").then((r) => setSub(r.data)).catch(() => {});
  const loadHist = () => api.get("/seller-pos/wallet/history").then((r) => setHist(r.data.rows || [])).catch(() => {});
  const loadProducts = (query = "") => api.get(`/seller-pos/products?q=${encodeURIComponent(query)}`).then((r) => setProducts(r.data)).catch(() => {});
  const loadSales = () => api.get("/seller-pos/sales").then((r) => setSales(r.data)).catch(() => {});
  useEffect(() => { loadSub(); loadHist(); }, []);
  useEffect(() => { if (sub?.active && sub?.is_seller) { loadProducts(); loadSales(); } }, [sub?.active, sub?.is_seller]);

  const topup = async (amount) => {
    if (!amount || amount < 50) { toast.error("Minimum ₹50 top-up"); return; }
    setBusy(true);
    try { const { data } = await api.post("/seller-pos/wallet/topup", { amount, origin_url: window.location.origin });
      if (data.url) window.location.href = data.url; else toast.error("Payment session nahi bana"); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const buyPlan = async (days) => {
    setBusy(true);
    try { const { data } = await api.post("/seller-pos/subscribe/wallet", { days });
      toast.success(`POS Premium active! ${data.days} din add hue.`); setRenew(false); loadSub(); loadHist(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const freeActivate = async (body, label) => {
    setBusy(true);
    try { const { data } = await api.post("/seller-pos/subscribe/free", body);
      toast.success(`${label} activate! ${data.days_left} din valid.`); setRenew(false); loadSub(); loadHist(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const setAuto = async (enabled, days) => {
    try { await api.put("/seller-pos/autorenew", { enabled, days: days || sub?.autorenew_days || 30 });
      toast.success(enabled ? "Auto-renew ON" : "Auto-renew OFF"); loadSub(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const add = (p) => setCart((c) => ({ ...c, [p.id]: { product: p, qty: Math.min((c[p.id]?.qty || 0) + 1, p.stock) } }));
  const dec = (id) => setCart((c) => { const q = (c[id]?.qty || 0) - 1; const n = { ...c }; if (q <= 0) delete n[id]; else n[id] = { ...n[id], qty: q }; return n; });
  const rm = (id) => setCart((c) => { const n = { ...c }; delete n[id]; return n; });

  const lines = Object.values(cart);
  const subtotal = lines.reduce((a, l) => a + l.product.price * l.qty, 0);
  const gst = lines.reduce((a, l) => a + (l.product.price * l.qty * (l.product.tax_rate || 0)) / 100, 0);
  const total = subtotal + gst;

  const completeSale = async () => {
    if (!lines.length) return;
    setBusy(true);
    try {
      const { data } = await api.post("/seller-pos/sale", { items: lines.map((l) => ({ product_id: l.product.id, qty: l.qty })), payment_method: "cash" });
      toast.success(`Bill ${data.bill_no} — ${inr(data.total)} done!`);
      setBill(data); setCart({}); loadProducts(q); loadSales();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const printBill = (b) => {
    const sb = b.seller_business || {};
    const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
    const rows = (b.items || []).map((it) => `<tr><td>${esc(it.name)}</td><td style="text-align:center">${it.qty}</td><td style="text-align:right">${inr(it.price)}</td><td style="text-align:right">${inr(it.line_total)}</td></tr>`).join("");
    const w = window.open("", "_blank", "width=380,height=640");
    if (!w) { toast.error("Popup block ho gaya — allow karein"); return; }
    w.document.write(`<html><head><title>${esc(b.bill_no)}</title><style>*{font-family:Arial,sans-serif}body{padding:14px;color:#111}h2{margin:0;color:#d81f26;font-size:18px}img.logo{display:block;margin:0 auto 6px;height:46px}table{width:100%;border-collapse:collapse;font-size:13px;margin-top:8px}td,th{padding:4px 2px;border-bottom:1px solid #eee}small{color:#555}.tot td{font-weight:800;border-top:2px solid #111}</style></head><body>
      <img class="logo" src="${window.location.origin}/rani-logo.png" onerror="this.style.display='none'"/>
      <h2>${esc(sb.legal_name || sb.business_name || "Rani Super Market Seller")}</h2>
      ${sb.gstin ? `<small>GSTIN: ${esc(sb.gstin)}</small><br/>` : ""}
      ${sb.address ? `<small>${esc(sb.address)}</small><br/>` : ""}
      ${sb.phone ? `<small>Ph: ${esc(sb.phone)}</small>` : ""}
      <hr/>
      <small>Bill: <b>${esc(b.bill_no)}</b> · ${esc(new Date(b.created_at).toLocaleString("en-IN"))}</small><br/>
      <small>Customer: ${esc(b.customer_name || "Walk-in")}</small>
      <table><thead><tr><th style="text-align:left">Item</th><th>Qty</th><th style="text-align:right">Rate</th><th style="text-align:right">Amt</th></tr></thead><tbody>${rows}</tbody></table>
      <table style="margin-top:6px"><tr><td>Subtotal</td><td style="text-align:right">${inr(b.subtotal)}</td></tr><tr><td>GST</td><td style="text-align:right">${inr(b.gst)}</td></tr><tr class="tot"><td>Total</td><td style="text-align:right">${inr(b.total)}</td></tr></table>
      <p style="text-align:center;margin-top:12px;font-size:12px;color:#555">Dhanyavaad! Phir aaiyega.</p>
      <p style="text-align:center;margin-top:2px;font-size:10px;color:#999">Powered by Rs. Mart POS</p>
      </body></html>`);
    w.document.close(); w.focus(); setTimeout(() => w.print(), 300);
  };

  if (!sub) return <div className="text-slate-400 text-sm">Loading…</div>;

  // Subscribe / Recharge screen (no active premium, or explicit renew)
  if (!sub.active || renew) {
    const bal = sub.wallet || 0;
    return (
      <div className="max-w-2xl mx-auto" data-testid="pos-subscribe">
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="flex items-center gap-2"><Calculator size={22} className="text-blue-600" /><h1 className="text-2xl font-bold">Rs Mart POS Premium</h1></div>
          {renew && <button data-testid="pos-renew-back" onClick={() => setRenew(false)} className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1"><ArrowLeft size={15} /> Back</button>}
        </div>
        <p className="text-slate-500 text-sm mb-5">Apni dukaan ka <b>billing + inventory</b> Rs Mart POS se chalayein. Sirf <b>₹1/din + GST</b>.</p>

        {/* Free 7-day trial (once per user) — arbitrary free-day self-grant removed (SEC) */}
        <div className="bg-gradient-to-br from-emerald-600 to-teal-700 text-white rounded-2xl p-5 mb-5" data-testid="pos-free-activate-card">
          <div className="flex items-center gap-2"><Crown size={18} /><span className="text-sm font-bold">7-din FREE trial</span></div>
          <p className="text-[11px] text-emerald-100 mt-1">Bina payment ke poora POS try karo. Trial ke baad neeche wallet se plan lo — sirf ₹1/din + GST.</p>
          <div className="flex flex-wrap gap-2 mt-3">
            <button data-testid="pos-free-trial" disabled={busy} onClick={() => freeActivate({ trial: true }, "7-din Free Trial")}
              className="px-4 py-2 bg-white text-emerald-700 rounded-lg font-extrabold text-sm disabled:opacity-50">7-Day Free Trial</button>
          </div>
        </div>

        <p className="text-xs font-bold text-slate-400 uppercase mb-2 mt-6">Wallet se plan lo (₹1/din)</p>

        {/* Wallet */}
        <div className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white rounded-2xl p-5 mb-5" data-testid="pos-wallet-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2"><Wallet size={18} /><span className="text-sm font-semibold text-blue-100">POS Wallet</span></div>
            <span className="text-2xl font-extrabold" data-testid="pos-wallet-balance">{inr(bal)}</span>
          </div>
          <p className="text-[11px] text-blue-100 mt-1">Balance se koi bhi plan lo. Online recharge minimum ₹50 (leftover next renewal ke liye bacha rahega).</p>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {[50, 100, 200, 500].map((a) => (
              <button key={a} data-testid={`pos-topup-chip-${a}`} onClick={() => setTopupAmt(a)} className={`px-3 py-1.5 rounded-lg text-sm font-bold transition-colors ${topupAmt === a ? "bg-white text-blue-700" : "bg-blue-500/40 text-white hover:bg-blue-500/60"}`}>₹{a}</button>
            ))}
            <input data-testid="pos-topup-input" type="number" min="50" value={topupAmt} onChange={(e) => setTopupAmt(Number(e.target.value))} className="w-24 px-3 py-1.5 rounded-lg text-slate-900 text-sm font-bold" />
            <button data-testid="pos-topup-btn" disabled={busy} onClick={() => topup(topupAmt)} className="px-4 py-1.5 bg-amber-400 hover:bg-amber-300 text-slate-900 rounded-lg font-extrabold text-sm disabled:opacity-50">Add Money</button>
          </div>
        </div>

        <p className="text-xs font-bold text-slate-400 uppercase mb-2">Plan chunein (wallet se)</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {sub.plans.map((p) => {
            const afford = bal >= p.total;
            return (
              <div key={p.days} data-testid={`pos-plan-${p.days}`} className="bg-white border-2 border-slate-100 hover:border-blue-400 rounded-2xl p-4 text-center transition-colors">
                <p className="text-xs font-bold text-slate-400 uppercase">{p.name}</p>
                <p className="text-2xl font-extrabold mt-1">{inr(p.total)}</p>
                <p className="text-[11px] text-slate-400">₹{p.price} + {inr(p.gst)} GST</p>
                <button data-testid={`pos-subscribe-${p.days}`} disabled={busy}
                  onClick={() => afford ? buyPlan(p.days) : toast.error(`Wallet me ₹${(p.total - bal).toFixed(2)} kam hai — Add Money karein`)}
                  className={`mt-3 w-full py-2 rounded-lg font-bold text-sm disabled:opacity-50 transition-colors ${afford ? "bg-blue-600 hover:bg-blue-700 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
                  {afford ? `Buy · ${p.days}d` : "Add Money"}
                </button>
              </div>
            );
          })}
        </div>

        {/* Auto-renew */}
        <div className="mt-5 bg-white border border-slate-200 rounded-2xl p-4" data-testid="pos-autorenew-card">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="font-bold text-sm flex items-center gap-1.5"><RefreshCw size={15} className="text-blue-600" /> Auto-Renew</p>
              <p className="text-[11px] text-slate-500 mt-0.5">Expiry se pehle wallet se apne aap renew — kabhi downtime nahi.</p>
            </div>
            <button data-testid="pos-autorenew-toggle" onClick={() => setAuto(!sub.autorenew)} className={`relative shrink-0 w-12 h-6 rounded-full transition-colors ${sub.autorenew ? "bg-emerald-500" : "bg-slate-300"}`}>
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${sub.autorenew ? "translate-x-6" : ""}`} />
            </button>
          </div>
          {sub.autorenew && (
            <div className="mt-3 flex items-center gap-2 text-sm">
              <span className="text-slate-500">Renew plan:</span>
              <select data-testid="pos-autorenew-days" value={sub.autorenew_days} onChange={(e) => setAuto(true, Number(e.target.value))} className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm font-semibold">
                {sub.plans.map((p) => <option key={p.days} value={p.days}>{p.name} · {inr(p.total)}</option>)}
              </select>
            </div>
          )}
        </div>

        {/* Wallet statement */}
        {hist.length > 0 && (
          <div className="mt-3 bg-white border border-slate-200 rounded-2xl p-4" data-testid="pos-wallet-history">
            <p className="font-bold text-sm mb-2">Wallet Statement</p>
            <div className="divide-y divide-slate-50">
              {hist.map((h) => (
                <div key={h.id} data-testid={`pos-wtxn-${h.id}`} className="flex items-center justify-between py-2 text-sm">
                  <div>
                    <p className="font-semibold">{h.type === "credit" ? "Recharge" : "Plan purchase"}{h.type === "debit" && h.ref ? ` · ${h.ref}` : ""}</p>
                    <p className="text-[11px] text-slate-400">{h.source} · {new Date(h.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
                  </div>
                  <span className={`font-extrabold ${h.type === "credit" ? "text-emerald-600" : "text-rose-500"}`}>{h.type === "credit" ? "+" : "−"}{inr(h.amount)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="text-[11px] text-slate-400 mt-4 text-center">Recharge ke baad wallet se plan turant activate. Test card: 4242 4242 4242 4242</p>
      </div>
    );
  }

  // Premium active but not an approved seller
  if (!sub.is_seller) {
    return (
      <div className="max-w-md mx-auto text-center bg-white border border-slate-200 rounded-2xl p-6 mt-6" data-testid="pos-need-seller">
        <Crown size={40} className="text-amber-500 mx-auto" />
        <h1 className="text-xl font-bold mt-2">POS Premium active hai 🎉</h1>
        <p className="text-slate-500 text-sm mt-1">Billing shuru karne ke liye pehle seller account banayein (apne products add karne ke liye).</p>
        <button data-testid="pos-apply-seller" onClick={() => nav("/become-seller")} className="mt-4 px-5 py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-bold">Apply to Sell</button>
      </div>
    );
  }

  // Active POS billing
  return (
    <div className="max-w-5xl mx-auto" data-testid="pos-billing">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div className="flex items-center gap-2"><Calculator size={22} className="text-blue-600" /><h1 className="text-2xl font-bold">Rs Mart POS</h1>
          <span className="text-[11px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">{sub.owner_exempt ? "OWNER" : `${sub.days_left}d left`}</span></div>
        <div className="flex items-center gap-2">
          {!sub.owner_exempt && (
            <button data-testid="pos-extend-btn" onClick={() => setRenew(true)} className="px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg text-sm font-semibold flex items-center gap-1">
              <Wallet size={15} /> {inr(sub.wallet || 0)} · Extend
            </button>
          )}
          <div className="text-right"><p className="text-[10px] text-slate-400 uppercase">Aaj ki sale</p><p className="font-extrabold text-emerald-600" data-testid="pos-today-total">{inr(sales.today_total)} · {sales.today_count}</p></div>
          <button data-testid="pos-inventory-link" onClick={() => nav("/seller/products")} className="px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-semibold flex items-center gap-1"><Boxes size={15} /> Inventory</button>
        </div>
      </div>

      {sub.seller_business && (
        <div data-testid="pos-firm-banner" className="mb-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
          <span className="font-bold text-sm text-slate-800">{sub.seller_business.legal_name || sub.seller_business.business_name}</span>
          {sub.seller_business.gstin && <span data-testid="pos-firm-gstin" className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">GSTIN {sub.seller_business.gstin}</span>}
          {sub.seller_business.gst_status === "verified" && <span className="text-[10px] font-bold text-emerald-700">✓ Verified</span>}
          {sub.seller_business.address && <span className="text-[11px] text-slate-500 truncate max-w-full">{sub.seller_business.address}</span>}
        </div>
      )}

      <div className="grid lg:grid-cols-5 gap-4">
        {/* Product picker */}
        <div className="lg:col-span-3 bg-white rounded-2xl border border-slate-100 p-3">
          <div className="flex gap-2 mb-3">
            <div className="relative flex-1"><Search size={16} className="absolute left-3 top-2.5 text-slate-400" />
              <input data-testid="pos-search" value={q} onChange={(e) => { setQ(e.target.value); loadProducts(e.target.value); }} placeholder="Product khojein…" className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm" /></div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-[60vh] overflow-y-auto">
            {products.map((p) => (
              <button key={p.id} data-testid={`pos-prod-${p.id}`} disabled={p.stock <= 0} onClick={() => add(p)} className="text-left border border-slate-100 rounded-xl p-2.5 hover:border-blue-300 disabled:opacity-40 transition-colors">
                <p className="font-semibold text-sm truncate">{p.name}</p>
                <p className="text-[11px] text-slate-400">Stock: {p.stock}</p>
                <p className="font-extrabold text-blue-700 text-sm mt-0.5">{inr(p.price)}</p>
              </button>
            ))}
            {!products.length && <p className="col-span-full text-center text-slate-400 text-sm py-8">Koi product nahi — Inventory me add karein.</p>}
          </div>
        </div>

        {/* Cart */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-100 p-3 flex flex-col">
          <div className="flex items-center gap-2 mb-2 font-bold text-sm"><ShoppingCart size={16} /> Bill ({lines.length})</div>
          <div className="flex-1 space-y-2 max-h-[40vh] overflow-y-auto">
            {lines.map((l) => (
              <div key={l.product.id} data-testid={`pos-cart-${l.product.id}`} className="flex items-center gap-2 text-sm border-b border-slate-50 pb-2">
                <div className="min-w-0 flex-1"><p className="font-semibold truncate">{l.product.name}</p><p className="text-[11px] text-slate-400">{inr(l.product.price)} × {l.qty} = {inr(l.product.price * l.qty)}</p></div>
                <button data-testid={`pos-dec-${l.product.id}`} onClick={() => dec(l.product.id)} className="p-1 bg-slate-100 rounded"><Minus size={13} /></button>
                <span className="w-5 text-center font-bold">{l.qty}</span>
                <button data-testid={`pos-inc-${l.product.id}`} onClick={() => add(l.product)} className="p-1 bg-slate-100 rounded"><Plus size={13} /></button>
                <button data-testid={`pos-rm-${l.product.id}`} onClick={() => rm(l.product.id)} className="p-1 text-rose-400"><Trash2 size={13} /></button>
              </div>
            ))}
            {!lines.length && <p className="text-center text-slate-400 text-sm py-8">Product tap karke bill banayein</p>}
          </div>
          <div className="border-t border-slate-100 pt-2 mt-2 text-sm space-y-1">
            <div className="flex justify-between text-slate-500"><span>Subtotal</span><span>{inr(subtotal)}</span></div>
            <div className="flex justify-between text-slate-500"><span>GST</span><span>{inr(gst)}</span></div>
            <div className="flex justify-between font-extrabold text-base"><span>Total</span><span data-testid="pos-total">{inr(total)}</span></div>
            <button data-testid="pos-complete-sale" disabled={busy || !lines.length} onClick={completeSale} className="mt-2 w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white rounded-xl font-bold flex items-center justify-center gap-2"><Receipt size={16} /> Complete Sale</button>
          </div>
        </div>
      </div>

      {/* Recent sales */}
      <div className="mt-4 bg-white rounded-2xl border border-slate-100 p-4">
        <h2 className="font-bold mb-2 text-sm">Recent Bills</h2>
        {!sales.sales.length ? <p className="text-slate-400 text-sm py-4 text-center">Abhi koi bill nahi bana.</p> : (
          <div className="divide-y divide-slate-50">
            {sales.sales.slice(0, 8).map((x) => (
              <div key={x.id} data-testid={`pos-sale-${x.id}`} className="flex items-center justify-between py-2 text-sm">
                <div><p className="font-semibold">{x.bill_no}</p><p className="text-[11px] text-slate-400">{x.items.length} items · {x.customer_name || "Walk-in"} · {new Date(x.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</p></div>
                <div className="flex items-center gap-2"><span className="font-extrabold">{inr(x.total)}</span>
                  <button data-testid={`pos-reprint-${x.id}`} onClick={() => setBill(x)} title="Print bill" className="p-1.5 text-slate-400 hover:text-slate-700"><Printer size={14} /></button></div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!bill} onOpenChange={(v) => !v && setBill(null)}>
        <DialogContent className="bg-white max-w-sm">
          <DialogHeader><DialogTitle>Bill {bill?.bill_no}</DialogTitle></DialogHeader>
          {bill && (<div data-testid="pos-receipt">
            <div className="text-center border-b border-slate-100 pb-2">
              <img src="/rani-logo.png" alt="Rani Super Market" className="h-9 mx-auto mb-1 object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
              <p className="font-extrabold text-[#d81f26]">{bill.seller_business?.legal_name || bill.seller_business?.business_name || "Seller"}</p>
              {bill.seller_business?.gstin && <p className="text-[11px] text-slate-500">GSTIN: {bill.seller_business.gstin}</p>}
              {bill.seller_business?.address && <p className="text-[11px] text-slate-500">{bill.seller_business.address}</p>}
            </div>
            <div className="text-[11px] text-slate-500 mt-2">Customer: {bill.customer_name || "Walk-in"}</div>
            <div className="mt-2 divide-y divide-slate-50 text-sm max-h-52 overflow-y-auto">
              {bill.items.map((it, i) => <div key={i} className="flex justify-between py-1"><span className="truncate pr-2">{it.name} × {it.qty}</span><span>{inr(it.line_total)}</span></div>)}
            </div>
            <div className="border-t border-slate-100 mt-2 pt-2 text-sm space-y-0.5">
              <div className="flex justify-between text-slate-500"><span>Subtotal</span><span>{inr(bill.subtotal)}</span></div>
              <div className="flex justify-between text-slate-500"><span>GST</span><span>{inr(bill.gst)}</span></div>
              <div className="flex justify-between font-extrabold"><span>Total</span><span>{inr(bill.total)}</span></div>
            </div>
            <button data-testid="pos-print-bill" onClick={() => printBill(bill)} className="mt-3 w-full py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-bold text-sm flex items-center justify-center gap-2"><Printer size={15} /> Print / Save Bill</button>
            <p className="text-center text-[10px] text-slate-400 mt-2">Powered by Rs. Mart POS</p>
          </div>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
